import React from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../lib/utils';
import { theme } from '../config/theme';

export default function Modal({ open, onClose, children, className, plain = false, ariaLabelledby }) {
    const [visible, setVisible] = React.useState(false);
    const panelRef = React.useRef(null);
    const lastFocusedRef = React.useRef(null);
    const onCloseRef = React.useRef(onClose);
    React.useEffect(() => { onCloseRef.current = onClose; }, [onClose]);
    React.useEffect(() => {
        // Плавное появление при каждом открытии
        if (open) {
            // Запомним последний фокус и установим фокус внутрь модалки
            try { lastFocusedRef.current = document.activeElement; } catch {}
            setVisible(false);
            const id = requestAnimationFrame(() => {
                setVisible(true);
                try {
                    const root = panelRef.current;
                    if (root) {
                        // Предпочитаем фокус на явном поле с data-autofocus
                        const preferred = root.querySelector('[data-autofocus="true"], [data-autofocus]');
                        if (preferred && typeof preferred.focus === 'function') {
                            preferred.focus();
                        } else {
                            const focusables = root.querySelectorAll('a[href], button, textarea, input, select, [tabindex]:not([tabindex="-1"])');
                        const first = focusables && focusables[0];
                        if (first && typeof first.focus === 'function') first.focus();
                        else if (typeof root.focus === 'function') root.focus();
                        }
                    }
                } catch {}
            });
            // Обработчик клавиш (Esc и Tab trap)
            const onKey = (e) => {
                if (!open) return;
                if (e.key === 'Escape') {
                    e.preventDefault();
                    try { onCloseRef.current?.(); } catch {}
                    return;
                }
                if (e.key === 'Tab') {
                    try {
                        const root = panelRef.current;
                        if (!root) return;
                        const nodes = root.querySelectorAll(
                            'a[href], button, textarea, input, select, [tabindex]:not([tabindex="-1"])'
                        );
                        if (!nodes || nodes.length === 0) return;
                        const focusables = Array.from(nodes).filter((n) => !n.hasAttribute('disabled'));
                        if (focusables.length === 0) return;
                        const first = focusables[0];
                        const last = focusables[focusables.length - 1];
                        if (e.shiftKey) {
                            if (document.activeElement === first) {
                                e.preventDefault(); last.focus();
                            }
                        } else {
                            if (document.activeElement === last) {
                                e.preventDefault(); first.focus();
                            }
                        }
                    } catch {}
                }
            };
            try { document.addEventListener('keydown', onKey); } catch {}
            return () => { cancelAnimationFrame(id); try { document.removeEventListener('keydown', onKey); } catch {} };
        }
        setVisible(false);
        // Восстановим фокус
        try { const el = lastFocusedRef.current; if (el && typeof el.focus === 'function') el.focus(); } catch {}
    }, [open]);

    if (!open) return null;
    const content = (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto" data-modal-content role="dialog" aria-modal="true" aria-labelledby={ariaLabelledby}>
            <div
                className="absolute inset-0"
                style={{
                    background: theme.mode === 'light' ? 'rgba(15,23,42,.35)' : 'rgba(0,0,0,.6)',
                    opacity: visible ? 1 : 0,
                    transition: 'opacity 260ms ease-out'
                }}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); try { onClose?.(); } catch {} }}
            />
            <div
                className={cn(
                    plain
                      ? 'relative z-10 p-0 rounded-none w-auto max-w-none max-h-[90vh] overflow-y-visible max-sm:w-screen max-sm:h-[100dvh] max-sm:max-h-[100dvh] max-sm:overflow-y-auto safe-bottom'
                      : 'relative z-10 rounded-xl p-4 xs:p-5 md:p-6 max-w-lg w-[92vw] max-h-[90vh] overflow-y-auto max-sm:w-screen max-sm:max-w-none max-sm:h-[100dvh] max-sm:max-h-[100dvh] max-sm:rounded-none safe-bottom',
                    className
                )}
                data-modal-panel
                ref={panelRef}
                tabIndex={-1}
                style={{
                    background: plain ? 'transparent' : theme.bgCard,
                    border: plain ? 'none' : `1px solid ${theme.border}`,
                    color: theme.text,
                    boxShadow: plain ? 'none' : theme.shadow,
                    opacity: visible ? 1 : 0,
                    transform: visible ? 'translateY(0) scale(1)' : 'translateY(8px) scale(0.985)',
                    transition: 'opacity 280ms ease-out, transform 300ms ease-out',
                    willChange: 'opacity, transform'
                }}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
            >
                {children}
            </div>
        </div>
    );
    return createPortal(content, document.body);
}
